/**
 * SDK Documentation Data
 *
 * Structured content for the token-kit SDK reference section.
 * Inspired by react.dev/reference — each entry can be rendered
 * on its own dedicated page or listed in the overview index.
 */

// ============================================================================
// Types
// ============================================================================

export interface SdkDocParam {
  name: string;
  type: string;
  required?: boolean;
  description: string;
  default?: string;
}

export interface SdkDocReturn {
  type: string;
  description: string;
}

export interface CodeExample {
  title: string;
  description?: string;
  code: string;
}

export interface SdkDocEntry {
  slug: string;
  title: string;
  category: string;
  kind: 'class' | 'method' | 'static-method' | 'type' | 'guide';
  summary: string;
  since?: string;
  signature?: string;
  params?: SdkDocParam[];
  returns?: SdkDocReturn;
  description: string;
  examples: CodeExample[];
  pitfalls?: string[];
  seeAlso?: string[];
}

export interface SdkDocCategory {
  name: string;
  slug: string;
  description: string;
  items: string[]; // slugs
}

// ============================================================================
// Categories
// ============================================================================

export const SDK_CATEGORIES: SdkDocCategory[] = [
  {
    name: 'Getting Started',
    slug: 'getting-started',
    description: 'Install, configure, and make your first API call.',
    items: ['installation', 'configuration'],
  },
  {
    name: 'TokenKit Class',
    slug: 'tokenkit-class',
    description: 'The main SDK class for interacting with token-kit APIs.',
    items: [
      'constructor',
      'chat',
      'validate-token',
      'get-balance',
      'get-models',
      'set-user-token',
    ],
  },
  {
    name: 'Helper Methods',
    slug: 'helpers',
    description: 'Static convenience methods for building messages.',
    items: ['helper-user', 'helper-system', 'helper-assistant'],
  },
  {
    name: 'Types',
    slug: 'types',
    description: 'TypeScript interfaces and types exported by the SDK.',
    items: [
      'type-tokenkit-config',
      'type-message',
      'type-chat-options',
      'type-chat-response',
      'type-token-validation-response',
      'type-tokenkit-api-error',
      'type-model-info',
      'type-api-response',
    ],
  },
  {
    name: 'Guides',
    slug: 'guides',
    description: 'In-depth guides for common integration patterns.',
    items: [
      'guide-error-handling',
      'guide-conversations',
      'guide-models',
      'guide-express',
      'guide-nextjs',
      'guide-best-practices',
    ],
  },
];

// ============================================================================
// All Entries
// ============================================================================

export const SDK_ENTRIES: SdkDocEntry[] = [
  // ── Getting Started ──────────────────────────────────
  {
    slug: 'installation',
    title: 'Installation',
    category: 'Getting Started',
    kind: 'guide',
    summary: 'Install the token-kit SDK via npm or yarn.',
    description:
      'The token-kit SDK is distributed as an npm package. It works with Node.js 16+, modern browsers (via bundlers), and edge runtimes.',
    examples: [
      {
        title: 'npm',
        code: `npm install @codetunezstudios/token-kit`,
      },
      {
        title: 'yarn',
        code: `yarn add @codetunezstudios/token-kit`,
      },
      {
        title: 'pnpm',
        code: `pnpm add @codetunezstudios/token-kit`,
      },
    ],
    pitfalls: [
      'The SDK includes axios as a dependency — you do not need to install it separately.',
      'In browser environments, never expose your developer API key in client-side code. Always proxy through your backend.',
    ],
    seeAlso: ['configuration', 'constructor'],
  },
  {
    slug: 'configuration',
    title: 'Configuration',
    category: 'Getting Started',
    kind: 'guide',
    summary: 'Configure the SDK with your API key and optional settings.',
    description:
      'Pass a `TokenKitConfig` object to the constructor. The only required field is `apiKey`. All requests will use these defaults.',
    examples: [
      {
        title: 'Minimal configuration',
        code: `import TokenKit from '@codetunezstudios/token-kit';

const tokenKit = new TokenKit({
  apiKey: 'dev_xxxxxxxx_your_api_key_here'
});`,
      },
      {
        title: 'Full configuration',
        code: `import TokenKit from '@codetunezstudios/token-kit';

const tokenKit = new TokenKit({
  apiKey: process.env.TOKENKIT_API_KEY!,
  baseUrl: 'https://api.token-kit.dev/api/v1',
  timeout: 30000, // 30 seconds
});`,
      },
      {
        title: 'Environment variables',
        description: 'Store sensitive values in environment variables.',
        code: `# .env
TOKENKIT_API_KEY=dev_xxxxxxxx_your_api_key
TOKENKIT_USER_TOKEN=ut_xxxxxxxx_user_token`,
      },
    ],
    seeAlso: ['constructor', 'type-tokenkit-config'],
  },

  // ── TokenKit Class ───────────────────────────────────
  {
    slug: 'constructor',
    title: 'TokenKit(config)',
    category: 'TokenKit Class',
    kind: 'class',
    summary: 'Create a new TokenKit client instance.',
    since: '1.0.0',
    signature: 'new TokenKit(config: TokenKitConfig)',
    params: [
      {
        name: 'config',
        type: 'TokenKitConfig',
        required: true,
        description: 'Configuration object. See TokenKitConfig type reference.',
      },
      {
        name: 'config.apiKey',
        type: 'string',
        required: true,
        description: 'Your developer API key from the token-kit dashboard.',
      },
      {
        name: 'config.baseUrl',
        type: 'string',
        required: false,
        description: 'API base URL.',
        default: 'https://api.token-kit.dev/api/v1',
      },
      {
        name: 'config.timeout',
        type: 'number',
        required: false,
        description: 'Request timeout in milliseconds.',
        default: '60000',
      },
    ],
    returns: {
      type: 'TokenKit',
      description: 'A configured TokenKit instance.',
    },
    description:
      'Creates a new TokenKit client. The API key is attached to every outbound request via the `Authorization: Bearer` header. Reuse a single instance across your application for best performance.',
    examples: [
      {
        title: 'Basic usage',
        code: `import TokenKit from '@codetunezstudios/token-kit';

const tokenKit = new TokenKit({
  apiKey: 'dev_xxxxxxxx_your_api_key_here'
});`,
      },
    ],
    pitfalls: [
      'Throws immediately if `apiKey` is missing or empty.',
      'Create one instance and reuse it — do not create a new instance per request.',
    ],
    seeAlso: ['type-tokenkit-config', 'configuration'],
  },

  {
    slug: 'chat',
    title: 'tokenKit.chat()',
    category: 'TokenKit Class',
    kind: 'method',
    summary: 'Send a chat completion request to an LLM.',
    since: '1.0.0',
    signature: `tokenKit.chat(userToken, messages, options?)
tokenKit.chat(messages, options?)  // if setUserToken() called`,
    params: [
      {
        name: 'userToken',
        type: 'string',
        required: false,
        description:
          'The end-user\'s token. Can be omitted if you called `setUserToken()` first.',
      },
      {
        name: 'messages',
        type: 'Message[]',
        required: true,
        description:
          'Array of conversation messages. Each message has a `role` and `content`.',
      },
      {
        name: 'options',
        type: 'ChatOptions',
        required: false,
        description: 'Optional parameters like model, maxTokens, and temperature.',
      },
    ],
    returns: {
      type: 'Promise<ChatResponse>',
      description:
        'Resolves with the assistant\'s reply, token usage stats, deduction amount, and remaining balance.',
    },
    description:
      'The primary method for making LLM requests. Supports overloaded signatures — pass the user token explicitly or set it once with `setUserToken()`. The SDK validates the user\'s balance server-side before forwarding to the LLM provider and deducts tokens only after a successful completion.',
    examples: [
      {
        title: 'Simple chat',
        code: `const response = await tokenKit.chat('user-token', [
  { role: 'user', content: 'What is token-kit?' }
]);

console.log(response.message.content);
console.log('Tokens used:', response.tokensDeducted);`,
      },
      {
        title: 'With system message and options',
        code: `const response = await tokenKit.chat('user-token', [
  TokenKit.system('You are a helpful coding tutor.'),
  TokenKit.user('Explain async/await in TypeScript')
], {
  model: 'gpt-4o',
  maxTokens: 300,
  temperature: 0.8
});`,
      },
      {
        title: 'Using setUserToken()',
        code: `tokenKit.setUserToken('user-token');

const response = await tokenKit.chat([
  TokenKit.user('Hello!')
]);`,
      },
    ],
    pitfalls: [
      'Throws if the messages array is empty.',
      'Throws if no user token is provided and none was set via `setUserToken()`.',
      'Token deduction only occurs after a successful LLM response — never pre-deducted.',
    ],
    seeAlso: [
      'set-user-token',
      'type-message',
      'type-chat-options',
      'type-chat-response',
      'helper-user',
      'helper-system',
    ],
  },

  {
    slug: 'validate-token',
    title: 'tokenKit.validateToken()',
    category: 'TokenKit Class',
    kind: 'method',
    summary: 'Validate a user token and retrieve balance & limits.',
    since: '1.0.0',
    signature: 'tokenKit.validateToken(userToken?: string): Promise<TokenValidationResponse>',
    params: [
      {
        name: 'userToken',
        type: 'string',
        required: false,
        description: 'User token to validate. Optional if `setUserToken()` was called.',
      },
    ],
    returns: {
      type: 'Promise<TokenValidationResponse>',
      description:
        'Token validity, user info, balance, spending history, and spending limits.',
    },
    description:
      'Checks whether a user token is valid and returns the associated balance and spending limits. Use this before expensive operations to verify the user can afford the request.',
    examples: [
      {
        title: 'Validate and check balance',
        code: `const validation = await tokenKit.validateToken('user-token');

if (!validation.valid) {
  throw new Error('Invalid token');
}

console.log('Balance:', validation.balance);
console.log('Daily spend:', validation.dailySpending);
console.log('Limits:', validation.limits);`,
      },
    ],
    pitfalls: [
      'Returns `{ valid: false }` for expired or revoked tokens — always check the `valid` field.',
    ],
    seeAlso: ['get-balance', 'type-token-validation-response'],
  },

  {
    slug: 'get-balance',
    title: 'tokenKit.getBalance()',
    category: 'TokenKit Class',
    kind: 'method',
    summary: 'Get the current token balance for a user.',
    since: '1.0.0',
    signature: 'tokenKit.getBalance(userToken?: string): Promise<number>',
    params: [
      {
        name: 'userToken',
        type: 'string',
        required: false,
        description: 'User token. Optional if `setUserToken()` was called.',
      },
    ],
    returns: {
      type: 'Promise<number>',
      description: 'Current token balance. Returns 0 if balance is unavailable.',
    },
    description:
      'A convenience wrapper around `validateToken()` that returns only the numeric balance. Ideal for displaying the user\'s remaining tokens in your UI.',
    examples: [
      {
        title: 'Check balance before request',
        code: `const balance = await tokenKit.getBalance('user-token');

if (balance < 100) {
  console.warn('Low balance! Please purchase more tokens.');
  return;
}

const response = await tokenKit.chat('user-token', messages);`,
      },
    ],
    seeAlso: ['validate-token'],
  },

  {
    slug: 'get-models',
    title: 'tokenKit.getModels()',
    category: 'TokenKit Class',
    kind: 'method',
    summary: 'Retrieve the list of available LLM models.',
    since: '1.0.0',
    signature: 'tokenKit.getModels(): Promise<string[]>',
    params: [],
    returns: {
      type: 'Promise<string[]>',
      description: 'Array of model identifier strings.',
    },
    description:
      'Fetches the current list of supported LLM models from the API. Use this to present model choices to your users or to validate a model before sending a chat request.',
    examples: [
      {
        title: 'List all models',
        code: `const models = await tokenKit.getModels();
// ['claude-3.5-haiku', 'claude-sonnet-4', 'nova-micro', 'nova-lite', 'gpt-4o', 'gpt-4o-mini']

console.log('Available models:', models);`,
      },
    ],
    seeAlso: ['chat', 'type-chat-options', 'guide-models'],
  },

  {
    slug: 'set-user-token',
    title: 'tokenKit.setUserToken()',
    category: 'TokenKit Class',
    kind: 'method',
    summary: 'Set the user token for all subsequent requests.',
    since: '1.0.0',
    signature: 'tokenKit.setUserToken(userToken: string): void',
    params: [
      {
        name: 'userToken',
        type: 'string',
        required: true,
        description: 'The end-user\'s token string.',
      },
    ],
    returns: {
      type: 'void',
      description: 'No return value.',
    },
    description:
      'Sets a default user token that will be used by `chat()`, `validateToken()`, and `getBalance()` when no explicit token is passed. This is useful in single-user contexts (e.g., CLI tools, single-tenant apps).',
    examples: [
      {
        title: 'Set once, use everywhere',
        code: `tokenKit.setUserToken('ut_abc123_user_token');

// No need to pass userToken on every call
const balance = await tokenKit.getBalance();
const response = await tokenKit.chat([
  TokenKit.user('Hello!')
]);`,
      },
    ],
    pitfalls: [
      'In multi-user applications (e.g., Express API), do NOT use `setUserToken()`. Pass the token explicitly per request instead.',
    ],
    seeAlso: ['chat', 'validate-token', 'get-balance'],
  },

  // ── Helper Methods ───────────────────────────────────
  {
    slug: 'helper-user',
    title: 'TokenKit.user()',
    category: 'Helper Methods',
    kind: 'static-method',
    summary: 'Create a user message object.',
    since: '1.0.0',
    signature: 'TokenKit.user(content: string): Message',
    params: [
      {
        name: 'content',
        type: 'string',
        required: true,
        description: 'The user\'s message text.',
      },
    ],
    returns: {
      type: 'Message',
      description: '`{ role: "user", content }` message object.',
    },
    description:
      'Static helper that creates a `Message` with `role: "user"`. Equivalent to writing `{ role: "user", content: "..." }` but more readable.',
    examples: [
      {
        title: 'Usage',
        code: `const msg = TokenKit.user('What is TypeScript?');
// { role: 'user', content: 'What is TypeScript?' }

await tokenKit.chat('token', [
  TokenKit.user('Explain token-kit.')
]);`,
      },
    ],
    seeAlso: ['helper-system', 'helper-assistant', 'type-message'],
  },
  {
    slug: 'helper-system',
    title: 'TokenKit.system()',
    category: 'Helper Methods',
    kind: 'static-method',
    summary: 'Create a system message object.',
    since: '1.0.0',
    signature: 'TokenKit.system(content: string): Message',
    params: [
      {
        name: 'content',
        type: 'string',
        required: true,
        description: 'The system instruction text.',
      },
    ],
    returns: {
      type: 'Message',
      description: '`{ role: "system", content }` message object.',
    },
    description:
      'Static helper that creates a `Message` with `role: "system"`. Use system messages to instruct the LLM about its persona and behavior.',
    examples: [
      {
        title: 'Usage',
        code: `await tokenKit.chat('token', [
  TokenKit.system('You are a friendly coding tutor.'),
  TokenKit.user('Explain closures.')
]);`,
      },
    ],
    seeAlso: ['helper-user', 'helper-assistant', 'type-message'],
  },
  {
    slug: 'helper-assistant',
    title: 'TokenKit.assistant()',
    category: 'Helper Methods',
    kind: 'static-method',
    summary: 'Create an assistant message object.',
    since: '1.0.0',
    signature: 'TokenKit.assistant(content: string): Message',
    params: [
      {
        name: 'content',
        type: 'string',
        required: true,
        description: 'The assistant\'s message text.',
      },
    ],
    returns: {
      type: 'Message',
      description: '`{ role: "assistant", content }` message object.',
    },
    description:
      'Static helper that creates a `Message` with `role: "assistant"`. Useful for injecting previous assistant responses into a conversation history.',
    examples: [
      {
        title: 'Building conversation history',
        code: `const history = [
  TokenKit.system('You are an assistant.'),
  TokenKit.user('What is 2+2?'),
  TokenKit.assistant('2 + 2 = 4.'),
  TokenKit.user('And 3+3?'),
];

const reply = await tokenKit.chat('token', history);`,
      },
    ],
    seeAlso: ['helper-user', 'helper-system', 'guide-conversations'],
  },

  // ── Types ────────────────────────────────────────────
  {
    slug: 'type-tokenkit-config',
    title: 'TokenKitConfig',
    category: 'Types',
    kind: 'type',
    summary: 'Configuration options for the TokenKit constructor.',
    since: '1.0.0',
    signature: `interface TokenKitConfig {
  apiKey: string;
  baseUrl?: string;
  timeout?: number;
}`,
    params: [
      { name: 'apiKey', type: 'string', required: true, description: 'Developer API key.' },
      {
        name: 'baseUrl',
        type: 'string',
        required: false,
        description: 'API base URL.',
        default: 'https://api.token-kit.dev/api/v1',
      },
      {
        name: 'timeout',
        type: 'number',
        required: false,
        description: 'Request timeout in ms.',
        default: '60000',
      },
    ],
    description: 'Passed to `new TokenKit(config)` to configure the client.',
    examples: [
      {
        title: 'Type usage',
        code: `import { TokenKitConfig } from '@codetunezstudios/token-kit';

const config: TokenKitConfig = {
  apiKey: process.env.TOKENKIT_API_KEY!,
  baseUrl: 'https://api.token-kit.dev/api/v1',
  timeout: 30000,
};`,
      },
    ],
    seeAlso: ['constructor'],
  },
  {
    slug: 'type-message',
    title: 'Message',
    category: 'Types',
    kind: 'type',
    summary: 'A single message in a conversation.',
    since: '1.0.0',
    signature: `type MessageRole = 'system' | 'user' | 'assistant';

interface Message {
  role: MessageRole;
  content: string;
}`,
    params: [
      {
        name: 'role',
        type: "MessageRole",
        required: true,
        description: "The sender role: 'system', 'user', or 'assistant'.",
      },
      { name: 'content', type: 'string', required: true, description: 'The message text.' },
    ],
    description:
      'Represents a single message in the conversation array passed to `chat()`. System messages set behavior, user messages are prompts, assistant messages are prior responses.',
    examples: [
      {
        title: 'Manual construction',
        code: `const messages: Message[] = [
  { role: 'system', content: 'You are a helpful assistant.' },
  { role: 'user', content: 'Hello!' },
];`,
      },
      {
        title: 'Using helpers',
        code: `const messages: Message[] = [
  TokenKit.system('You are a helpful assistant.'),
  TokenKit.user('Hello!'),
];`,
      },
    ],
    seeAlso: ['helper-user', 'helper-system', 'helper-assistant', 'chat'],
  },
  {
    slug: 'type-chat-options',
    title: 'ChatOptions',
    category: 'Types',
    kind: 'type',
    summary: 'Optional parameters for chat requests.',
    since: '1.0.0',
    signature: `interface ChatOptions {
  model?: string;
  maxTokens?: number;
  temperature?: number;
}`,
    params: [
      {
        name: 'model',
        type: 'string',
        required: false,
        description: 'LLM model identifier.',
        default: 'gpt-4o-mini',
      },
      {
        name: 'maxTokens',
        type: 'number',
        required: false,
        description: 'Maximum tokens in the response.',
        default: '500',
      },
      {
        name: 'temperature',
        type: 'number',
        required: false,
        description: 'Randomness of output, from 0 (deterministic) to 2 (creative).',
        default: '0.7',
      },
    ],
    description:
      'Controls the LLM model, output length, and creativity. Pass as the last argument to `chat()`.',
    examples: [
      {
        title: 'Precise, short response',
        code: `const response = await tokenKit.chat('token', messages, {
  model: 'gpt-4o-mini',
  maxTokens: 100,
  temperature: 0.2,
});`,
      },
      {
        title: 'Creative, longer response',
        code: `const response = await tokenKit.chat('token', messages, {
  model: 'claude-sonnet-4',
  maxTokens: 1000,
  temperature: 1.5,
});`,
      },
    ],
    seeAlso: ['chat', 'guide-models'],
  },
  {
    slug: 'type-chat-response',
    title: 'ChatResponse',
    category: 'Types',
    kind: 'type',
    summary: 'The response returned from a chat completion.',
    since: '1.0.0',
    signature: `interface ChatResponse {
  id: string;
  model: string;
  message: Message;
  tokensUsed: {
    prompt: number;
    completion: number;
    total: number;
  };
  tokensDeducted: number;
  finishReason: string;
  userBalance?: number;
  latency?: number;
}`,
    params: [
      { name: 'id', type: 'string', required: true, description: 'Unique completion ID.' },
      { name: 'model', type: 'string', required: true, description: 'Model used for this completion.' },
      { name: 'message', type: 'Message', required: true, description: "The assistant's response message." },
      {
        name: 'tokensUsed',
        type: '{ prompt, completion, total }',
        required: true,
        description: 'Breakdown of token usage (prompt, completion, total).',
      },
      {
        name: 'tokensDeducted',
        type: 'number',
        required: true,
        description: 'Actual tokens deducted from user balance (includes model rate multiplier).',
      },
      { name: 'finishReason', type: 'string', required: true, description: "Why the completion ended (e.g. 'stop', 'length')." },
      { name: 'userBalance', type: 'number', required: false, description: 'Remaining user balance after deduction.' },
      { name: 'latency', type: 'number', required: false, description: 'Server-side response latency in milliseconds.' },
    ],
    description:
      'Contains everything you need after a successful chat request: the reply, token accounting, and performance info.',
    examples: [
      {
        title: 'Accessing the response',
        code: `const response = await tokenKit.chat('token', messages);

console.log(response.message.content);   // The reply text
console.log(response.tokensDeducted);     // Cost in tokens
console.log(response.userBalance);        // Remaining balance
console.log(response.latency + 'ms');     // Response time`,
      },
    ],
    seeAlso: ['chat', 'type-message'],
  },
  {
    slug: 'type-token-validation-response',
    title: 'TokenValidationResponse',
    category: 'Types',
    kind: 'type',
    summary: 'Response from token validation including balance and limits.',
    since: '1.0.0',
    signature: `interface TokenValidationResponse {
  valid: boolean;
  user?: { id: string; email: string };
  balance?: number;
  dailySpending?: number;
  monthlySpending?: number;
  limits?: {
    dailyLimit: number;
    monthlyLimit: number;
    perRequestLimit: number;
  };
}`,
    params: [
      { name: 'valid', type: 'boolean', required: true, description: 'Whether the token is valid and active.' },
      { name: 'user', type: '{ id, email }', required: false, description: 'Basic user info if token is valid.' },
      { name: 'balance', type: 'number', required: false, description: 'Current token balance.' },
      { name: 'dailySpending', type: 'number', required: false, description: 'Tokens spent today.' },
      { name: 'monthlySpending', type: 'number', required: false, description: 'Tokens spent this month.' },
      {
        name: 'limits',
        type: '{ dailyLimit, monthlyLimit, perRequestLimit }',
        required: false,
        description: 'Spending limits configured for the user.',
      },
    ],
    description:
      'The full validation result, including user identity, balance, spending history, and configured limits.',
    examples: [
      {
        title: 'Full validation check',
        code: `const v = await tokenKit.validateToken('user-token');

if (v.valid && v.balance && v.balance > 0) {
  console.log(\`User \${v.user?.email} has \${v.balance} tokens.\`);
  console.log(\`Daily limit: \${v.limits?.dailyLimit}\`);
}`,
      },
    ],
    seeAlso: ['validate-token', 'get-balance'],
  },
  {
    slug: 'type-tokenkit-api-error',
    title: 'TokenKitAPIError',
    category: 'Types',
    kind: 'type',
    summary: 'Custom error class thrown by all SDK methods on failure.',
    since: '1.0.0',
    signature: `class TokenKitAPIError extends Error {
  code: string;
  statusCode?: number;
  details?: unknown;

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    details?: unknown
  );
}`,
    params: [
      { name: 'message', type: 'string', required: true, description: 'Human-readable error message.' },
      { name: 'code', type: 'string', required: true, description: 'Machine-readable error code (e.g. INSUFFICIENT_BALANCE).' },
      { name: 'statusCode', type: 'number', required: false, description: 'HTTP status code from the API.' },
      { name: 'details', type: 'unknown', required: false, description: 'Additional error context from the API.' },
    ],
    description:
      'All API errors are normalized into `TokenKitAPIError` instances. Use `error.code` for programmatic handling and `error.message` for display.',
    examples: [
      {
        title: 'Catching errors',
        code: `import { TokenKitAPIError } from '@codetunezstudios/token-kit';

try {
  await tokenKit.chat('token', messages);
} catch (error) {
  if (error instanceof TokenKitAPIError) {
    console.error(error.code);       // 'INSUFFICIENT_BALANCE'
    console.error(error.statusCode); // 402
    console.error(error.message);    // 'User has insufficient tokens'
  }
}`,
      },
    ],
    seeAlso: ['guide-error-handling'],
  },
  {
    slug: 'type-model-info',
    title: 'ModelInfo',
    category: 'Types',
    kind: 'type',
    summary: 'Metadata about a supported LLM model.',
    since: '1.0.0',
    signature: `interface ModelInfo {
  name: string;
  displayName: string;
  tokenRate: number;
  defaultMaxTokens: number;
}`,
    params: [
      { name: 'name', type: 'string', required: true, description: 'Model identifier used in API calls.' },
      { name: 'displayName', type: 'string', required: true, description: 'Human-readable model name.' },
      { name: 'tokenRate', type: 'number', required: true, description: 'Token rate multiplier (1.0x, 2.0x, 3.0x).' },
      { name: 'defaultMaxTokens', type: 'number', required: true, description: 'Default max tokens for this model.' },
    ],
    description: 'Describes a supported LLM model including its pricing rate multiplier.',
    examples: [
      {
        title: 'Model pricing reference',
        code: `// Model rates
// gpt-4o-mini      → 1.0x (100 tokens = 100 deducted)
// claude-3.5-haiku  → 1.0x (100 tokens = 100 deducted)
// nova-micro        → 1.0x (100 tokens = 100 deducted)
// nova-lite         → 1.0x (100 tokens = 100 deducted)
// gpt-4o            → 2.0x (100 tokens = 200 deducted)
// claude-sonnet-4   → 3.0x (100 tokens = 300 deducted)`,
      },
    ],
    seeAlso: ['guide-models', 'get-models'],
  },
  {
    slug: 'type-api-response',
    title: 'ApiResponse<T>',
    category: 'Types',
    kind: 'type',
    summary: 'Standard envelope for all API responses.',
    since: '1.0.0',
    signature: `interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  meta?: {
    requestId: string;
    timestamp: string;
    latency?: number;
  };
}`,
    params: [
      { name: 'success', type: 'boolean', required: true, description: 'Whether the request succeeded.' },
      { name: 'data', type: 'T', required: false, description: 'Response payload (present on success).' },
      { name: 'error', type: '{ code, message, details? }', required: false, description: 'Error details (present on failure).' },
      { name: 'meta', type: '{ requestId, timestamp, latency? }', required: false, description: 'Request metadata for debugging.' },
    ],
    description:
      'The raw response envelope from the token-kit API. The SDK unwraps this automatically — you only interact with `ApiResponse` if building custom integrations.',
    examples: [
      {
        title: 'Shape example',
        code: `// Success
{
  success: true,
  data: { /* ChatResponse, etc. */ },
  meta: {
    requestId: 'req_abc123',
    timestamp: '2026-03-20T12:00:00Z',
    latency: 245
  }
}

// Error
{
  success: false,
  error: {
    code: 'INSUFFICIENT_BALANCE',
    message: 'User has insufficient tokens'
  }
}`,
      },
    ],
    seeAlso: ['type-tokenkit-api-error'],
  },

  // ── Guides ───────────────────────────────────────────
  {
    slug: 'guide-error-handling',
    title: 'Error Handling',
    category: 'Guides',
    kind: 'guide',
    summary: 'Handle all SDK errors gracefully with typed error codes.',
    description:
      'Every SDK method that communicates with the API can throw a `TokenKitAPIError`. This guide covers all error codes, recommended handling patterns, and retry strategies.',
    examples: [
      {
        title: 'Comprehensive error handling',
        code: `import { TokenKitAPIError } from '@codetunezstudios/token-kit';

try {
  const response = await tokenKit.chat('user-token', messages);
} catch (error) {
  if (error instanceof TokenKitAPIError) {
    switch (error.code) {
      case 'INSUFFICIENT_BALANCE':
        // Prompt user to buy more tokens
        showPurchaseDialog();
        break;
      case 'INVALID_TOKEN':
        // Re-authenticate the user
        redirectToLogin();
        break;
      case 'RATE_LIMIT_EXCEEDED':
        // Back off and retry
        await sleep(1000);
        break;
      case 'DAILY_LIMIT_EXCEEDED':
      case 'MONTHLY_LIMIT_EXCEEDED':
        showLimitWarning(error.code);
        break;
      case 'TIMEOUT':
        showRetryButton();
        break;
      case 'NETWORK_ERROR':
        showOfflineMessage();
        break;
      default:
        console.error('Unexpected:', error.message);
    }
  }
}`,
      },
      {
        title: 'Error codes reference',
        description: 'All possible error codes from the token-kit API.',
        code: `// Authentication errors
'INVALID_API_KEY'      // 401 – Developer API key is invalid
'INVALID_TOKEN'        // 401 – User token is invalid or expired

// Balance / limits
'INSUFFICIENT_BALANCE' // 402 – User lacks tokens for request
'DAILY_LIMIT_EXCEEDED' // 429 – User hit daily spending limit
'MONTHLY_LIMIT_EXCEEDED' // 429 – User hit monthly spending limit
'RATE_LIMIT_EXCEEDED'  // 429 – Too many requests per second

// Request errors
'VALIDATION_ERROR'     // 400 – Invalid request body
'MODEL_NOT_SUPPORTED'  // 400 – Unknown model identifier
'CHAT_ERROR'           // 500 – LLM provider error

// Network / infrastructure
'TIMEOUT'              // 504 – Request exceeded timeout
'NETWORK_ERROR'        // 503 – Cannot reach token-kit API`,
      },
    ],
    seeAlso: ['type-tokenkit-api-error', 'chat', 'validate-token'],
  },
  {
    slug: 'guide-conversations',
    title: 'Multi-Turn Conversations',
    category: 'Guides',
    kind: 'guide',
    summary: 'Build multi-turn conversations by managing message history.',
    description:
      'LLMs are stateless — the SDK sends the full message history on every request. To create a multi-turn conversation, append each response to your messages array and re-send the full context.',
    examples: [
      {
        title: 'Multi-turn conversation',
        code: `import TokenKit, { Message } from '@codetunezstudios/token-kit';

const tokenKit = new TokenKit({ apiKey: process.env.TOKENKIT_API_KEY! });
const history: Message[] = [
  TokenKit.system('You are a friendly coding tutor.')
];

// Turn 1
history.push(TokenKit.user('What is TypeScript?'));
let reply = await tokenKit.chat('user-token', history);
history.push(reply.message);
console.log('Assistant:', reply.message.content);

// Turn 2 (has full context)
history.push(TokenKit.user('How is it different from JavaScript?'));
reply = await tokenKit.chat('user-token', history);
history.push(reply.message);
console.log('Assistant:', reply.message.content);

// Turn 3
history.push(TokenKit.user('Show me an example.'));
reply = await tokenKit.chat('user-token', history);
history.push(reply.message);
console.log('Assistant:', reply.message.content);`,
      },
      {
        title: 'Conversation with token tracking',
        code: `let totalCost = 0;

for (const question of questions) {
  history.push(TokenKit.user(question));
  const reply = await tokenKit.chat('user-token', history);
  history.push(reply.message);
  totalCost += reply.tokensDeducted;
  
  console.log(\`Cost: \${reply.tokensDeducted} tokens\`);
  console.log(\`Remaining: \${reply.userBalance} tokens\`);
}

console.log(\`Total conversation cost: \${totalCost} tokens\`);`,
      },
    ],
    pitfalls: [
      'Longer conversations consume more tokens per request because the entire history is sent each time.',
      'Consider truncating old messages or summarizing them to control costs.',
    ],
    seeAlso: ['chat', 'helper-user', 'helper-system', 'helper-assistant'],
  },
  {
    slug: 'guide-models',
    title: 'Model Selection',
    category: 'Guides',
    kind: 'guide',
    summary: 'Choose the right LLM model for your use case and budget.',
    description:
      'token-kit supports multiple LLM models with different capabilities and pricing tiers. The model you choose affects response quality, speed, and token cost via a rate multiplier.',
    examples: [
      {
        title: 'Model comparison',
        code: `// GPT-4o Mini — Fast & affordable (1.0x rate)
// Best for: chatbots, simple Q&A, summaries
const fast = await tokenKit.chat('token', messages, {
  model: 'gpt-4o-mini'
});

// GPT-4o — Balanced (2.0x rate)
// Best for: code generation, analysis, complex tasks
const balanced = await tokenKit.chat('token', messages, {
  model: 'gpt-4o'
});

// Claude Sonnet 4 — Most capable (3.0x rate)
// Best for: reasoning, research, nuanced tasks
const premium = await tokenKit.chat('token', messages, {
  model: 'claude-sonnet-4',
  maxTokens: 500
});`,
      },
      {
        title: 'Dynamic model selection',
        code: `const models = await tokenKit.getModels();

// Let users choose
function selectModel(preference: 'fast' | 'balanced' | 'best') {
  switch (preference) {
    case 'fast':     return 'gpt-4o-mini';
    case 'balanced': return 'gpt-4o';
    case 'best':     return 'claude-sonnet-4';
  }
}

const response = await tokenKit.chat('token', messages, {
  model: selectModel('balanced')
});`,
      },
      {
        title: 'Token rate multipliers',
        description: 'How model rates affect token deduction.',
        code: `// If an LLM request uses 100 raw tokens:
//
// ┌──────────────────┬──────┬──────────────────────┐
// │ Model            │ Rate │ Tokens Deducted      │
// ├──────────────────┼──────┼──────────────────────┤
// │ gpt-4o-mini      │ 1.0x │ 100                  │
// │ claude-3.5-haiku  │ 1.0x │ 100                  │
// │ gpt-4o            │ 2.0x │ 200                  │
// │ claude-sonnet-4   │ 3.0x │ 300                  │
// └──────────────────┴──────┴──────────────────────┘`,
      },
    ],
    seeAlso: ['chat', 'type-chat-options', 'get-models', 'type-model-info'],
  },
  {
    slug: 'guide-express',
    title: 'Express.js Integration',
    category: 'Guides',
    kind: 'guide',
    summary: 'Integrate token-kit into an Express.js backend.',
    description:
      'The most common integration pattern: an Express API that receives chat requests from your frontend and proxies them through token-kit. This keeps your API key on the server.',
    examples: [
      {
        title: 'Express chat endpoint',
        code: `import express from 'express';
import TokenKit, { TokenKitAPIError } from '@codetunezstudios/token-kit';

const app = express();
app.use(express.json());

const tokenKit = new TokenKit({
  apiKey: process.env.TOKENKIT_API_KEY!
});

app.post('/api/chat', async (req, res) => {
  try {
    const { userToken, message } = req.body;

    // Validate token & check balance
    const validation = await tokenKit.validateToken(userToken);
    if (!validation.valid) {
      return res.status(401).json({ error: 'Invalid token' });
    }
    if ((validation.balance || 0) < 10) {
      return res.status(402).json({
        error: 'Insufficient balance',
        balance: validation.balance
      });
    }

    // Make the chat request
    const response = await tokenKit.chat(userToken, [
      TokenKit.system('You are a helpful assistant.'),
      TokenKit.user(message)
    ]);

    res.json({
      reply: response.message.content,
      tokensUsed: response.tokensDeducted,
      balance: response.userBalance,
    });
  } catch (error) {
    if (error instanceof TokenKitAPIError) {
      res.status(error.statusCode || 500).json({
        error: error.message,
        code: error.code
      });
    } else {
      res.status(500).json({ error: 'Internal server error' });
    }
  }
});

app.listen(3000);`,
      },
      {
        title: 'Health check endpoint',
        code: `app.get('/api/health', async (req, res) => {
  try {
    const models = await tokenKit.getModels();
    res.json({ status: 'healthy', models: models.length });
  } catch {
    res.status(503).json({ status: 'unhealthy' });
  }
});`,
      },
    ],
    pitfalls: [
      'Never expose your developer API key to the frontend — always keep it server-side.',
      'Do NOT call `setUserToken()` in a multi-user Express app. Pass the user token per request.',
    ],
    seeAlso: ['chat', 'validate-token', 'guide-best-practices'],
  },
  {
    slug: 'guide-nextjs',
    title: 'Next.js Integration',
    category: 'Guides',
    kind: 'guide',
    summary: 'Use token-kit in Next.js API routes and server components.',
    description:
      'Next.js API routes run server-side, making them ideal for token-kit integration. Your API key stays on the server while the frontend communicates through your route.',
    examples: [
      {
        title: 'Next.js API route (pages router)',
        code: `// pages/api/chat.ts
import type { NextApiRequest, NextApiResponse } from 'next';
import TokenKit from '@codetunezstudios/token-kit';

const tokenKit = new TokenKit({
  apiKey: process.env.TOKENKIT_API_KEY!
});

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  if (req.method !== 'POST') {
    return res.status(405).end();
  }

  const { userToken, message } = req.body;

  try {
    const response = await tokenKit.chat(userToken, [
      TokenKit.user(message)
    ]);

    res.json({
      reply: response.message.content,
      tokensUsed: response.tokensDeducted,
      balance: response.userBalance,
    });
  } catch (error: any) {
    res.status(error.statusCode || 500).json({
      error: error.message
    });
  }
}`,
      },
      {
        title: 'Next.js Route Handler (app router)',
        code: `// app/api/chat/route.ts
import { NextRequest, NextResponse } from 'next/server';
import TokenKit from '@codetunezstudios/token-kit';

const tokenKit = new TokenKit({
  apiKey: process.env.TOKENKIT_API_KEY!
});

export async function POST(request: NextRequest) {
  const { userToken, message } = await request.json();

  const response = await tokenKit.chat(userToken, [
    TokenKit.user(message)
  ]);

  return NextResponse.json({
    reply: response.message.content,
    tokensUsed: response.tokensDeducted,
    balance: response.userBalance,
  });
}`,
      },
    ],
    seeAlso: ['guide-express', 'guide-best-practices'],
  },
  {
    slug: 'guide-best-practices',
    title: 'Best Practices',
    category: 'Guides',
    kind: 'guide',
    summary: 'Recommended patterns for production token-kit integrations.',
    description:
      'Follow these guidelines for secure, efficient, and maintainable token-kit integrations.',
    examples: [
      {
        title: '1. Reuse the client instance',
        description: 'Create one TokenKit instance and share it across your application.',
        code: `// ✅ Good — single instance, shared
const tokenKit = new TokenKit({ apiKey });
export default tokenKit;

// ❌ Bad — new instance on every request
function chat() {
  const tk = new TokenKit({ apiKey });
  return tk.chat(...);
}`,
      },
      {
        title: '2. Always check balance first',
        description: 'Avoid failed LLM requests by checking balance upfront.',
        code: `const balance = await tokenKit.getBalance(userToken);
if (balance < estimatedCost) {
  throw new Error('Insufficient balance');
}
const response = await tokenKit.chat(userToken, messages);`,
      },
      {
        title: '3. Use environment variables',
        description: 'Never hardcode API keys.',
        code: `// .env
TOKENKIT_API_KEY=dev_xxxxxxxx_your_api_key

// app.ts
const tokenKit = new TokenKit({
  apiKey: process.env.TOKENKIT_API_KEY!
});`,
      },
      {
        title: '4. Set appropriate maxTokens',
        description: 'Prevent excessive usage and cost surprises.',
        code: `const response = await tokenKit.chat(userToken, messages, {
  maxTokens: 200,  // Cap the response length
  temperature: 0.7
});`,
      },
      {
        title: '5. Handle errors at every call site',
        description: 'Every API call can fail — handle it.',
        code: `try {
  const response = await tokenKit.chat(userToken, messages);
  return response.message.content;
} catch (error) {
  if (error instanceof TokenKitAPIError) {
    logger.error({ code: error.code, status: error.statusCode });
    throw new AppError('AI request failed', error.code);
  }
  throw error;
}`,
      },
      {
        title: '6. Keep API keys server-side',
        description: 'Never expose keys in browser code.',
        code: `// ❌ Bad — key exposed in browser bundle
const tokenKit = new TokenKit({ apiKey: 'dev_xxx' });

// ✅ Good — proxy through your backend
const res = await fetch('/api/chat', {
  method: 'POST',
  body: JSON.stringify({ userToken, message })
});`,
      },
    ],
    seeAlso: ['guide-error-handling', 'guide-express', 'guide-nextjs'],
  },
];

// ============================================================================
// Lookup helpers
// ============================================================================

const ENTRY_MAP = new Map(SDK_ENTRIES.map((e) => [e.slug, e]));

export function getEntry(slug: string): SdkDocEntry | undefined {
  return ENTRY_MAP.get(slug);
}

export function getEntriesByCategory(categoryName: string): SdkDocEntry[] {
  return SDK_ENTRIES.filter((e) => e.category === categoryName);
}
